/**
 * Tiny external store of background tasks, shown by TaskConsole.
 * Framework-free so lib/worker-pool.js can register tasks without touching React;
 * components read it through `useTaskStore()` (useSyncExternalStore).
 */
import { useSyncExternalStore } from 'react';

const MAX_LOG = 100;
const MAX_FINISHED = 20;

let nextId = 1;
let state = { tasks: [], log: [], version: 0 };
const listeners = new Set();

function emit(patch) {
  state = { ...state, ...patch, version: state.version + 1 };
  listeners.forEach(l => l());
}

const clock = () => new Date().toLocaleTimeString();

function addLog(type, msg) {
  return [...state.log.slice(-(MAX_LOG - 1)), { time: clock(), type, msg }];
}

/** Register a running task. Returns a handle { id, update, done, fail, cancelled }. */
export function startTask(label, { cancel } = {}) {
  const id = nextId++;
  const task = { id, label, status: 'running', progress: 0, message: '', cancel: cancel || null };
  emit({ tasks: [...state.tasks, task], log: addLog('SYSTEM', `${label} started`) });

  const finish = (status, type, msg) => {
    const cur = state.tasks.find(t => t.id === id);
    if (!cur || cur.status !== 'running') return;
    const tasks = state.tasks.map(t => (t.id === id
      ? { ...t, status, progress: status === 'done' ? 1 : t.progress, cancel: null, message: msg || t.message }
      : t));
    // keep the list short: drop the oldest finished tasks
    const finished = tasks.filter(t => t.status !== 'running');
    const drop = new Set(finished.slice(0, Math.max(0, finished.length - MAX_FINISHED)).map(t => t.id));
    emit({ tasks: tasks.filter(t => !drop.has(t.id)), log: addLog(type, `${label}${msg ? `: ${msg}` : ''}`) });
  };

  return {
    id,
    update(progress, message) {
      const cur = state.tasks.find(t => t.id === id);
      if (!cur || cur.status !== 'running') return;
      const p = Math.max(0, Math.min(1, progress ?? cur.progress));
      emit({ tasks: state.tasks.map(t => (t.id === id ? { ...t, progress: p, message: message ?? t.message } : t)) });
    },
    done(msg) { finish('done', 'DONE', msg || 'done'); },
    fail(msg) { finish('failed', 'ERROR', msg); },
    cancelled() { finish('cancelled', 'WARN', 'cancelled'); },
  };
}

export const getTaskState = () => state;
export function subscribeTasks(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Counts in the shape TaskConsole's `tasks` prop expects. */
export function taskCounts(tasks) {
  return {
    running: tasks.filter(t => t.status === 'running').length,
    completed: tasks.filter(t => t.status === 'done').length,
    failed: tasks.filter(t => t.status === 'failed').length,
  };
}

export function useTaskStore() {
  return useSyncExternalStore(subscribeTasks, getTaskState, getTaskState);
}

/** Test helper. */
export function resetTaskStore() {
  state = { tasks: [], log: [], version: state.version + 1 };
  listeners.forEach(l => l());
}
